import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from '../../lib/routing';
import { onboardingDoneTarget, onboardingHref, PROFILE_INCOMPLETE_REASON, safeTeacherNext } from '../../lib/onboarding-return';
import { api } from '../../lib/api';
import { authClient } from '../../lib/auth-client';

export default function TeacherOnboardingWelcome() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawNext = searchParams.get('next');
  const next = safeTeacherNext(rawNext);
  const profileIncomplete = next !== null && searchParams.get('reason') === PROFILE_INCOMPLETE_REASON;
  const [firstName, setFirstName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await api.onboarding.status.query();
        if (cancelled) return;
        if (!status || status.onboarding_completed) {
          navigate(onboardingDoneTarget(rawNext), { replace: true });
          return;
        }
        if (status.onboarding_step === 'classes') {
          navigate(onboardingHref('/onboarding/classes', next), { replace: true });
          return;
        }
        if (status.onboarding_step === 'prefs') {
          navigate(onboardingHref('/onboarding/preferences', next), { replace: true });
          return;
        }
        setFirstName(status.first_name ?? null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, rawNext, next]);

  const handleStart = async () => {
    setBusy(true);
    try {
      await api.onboarding.start.mutate();
      navigate(onboardingHref('/onboarding/classes', next));
    } finally {
      setBusy(false);
    }
  };

  const handleSignOut = async () => {
    await authClient.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-between min-h-[100dvh] pt-[max(24px,env(safe-area-inset-top))] pr-[max(24px,env(safe-area-inset-right))] pb-[max(24px,env(safe-area-inset-bottom))] pl-[max(24px,env(safe-area-inset-left))] bg-secondary">
        <div className="w-10 h-10 rounded-full border-[3px] border-border border-t-naia-blue [animation:spin_0.8s_linear_infinite]" aria-label="Chargement…" />
      </div>
    );
  }

  return (
    <div className="flex flex-col justify-between min-h-[100dvh] pt-[max(24px,env(safe-area-inset-top))] pr-[max(24px,env(safe-area-inset-right))] pb-[max(24px,env(safe-area-inset-bottom))] pl-[max(24px,env(safe-area-inset-left))] bg-secondary">
      <div className="flex-1 flex flex-col items-center justify-center gap-4 text-center py-6">
        <div className="text-[72px] leading-none" aria-hidden>🎓</div>
        <h1 className="font-black text-[28px] text-foreground m-0">Bienvenue sur Naïa</h1>
        {profileIncomplete ? (
          <p role="status" className="font-bold text-[15px] text-foreground bg-naia-info-bg rounded-[12px] px-4 py-3 max-w-[320px] m-0">
            Terminez votre profil pour accéder à l'assistant
          </p>
        ) : null}
        <p className="font-[500] text-[16px] text-muted-foreground leading-[1.6] max-w-[320px] m-0">
          {firstName
            ? `Bonjour ${firstName} ! Prenons quelques instants pour configurer votre espace.`
            : 'Prenons quelques instants pour configurer votre espace.'}
        </p>
        <p className="font-semibold text-sm text-muted-foreground leading-[1.5] max-w-[300px] m-0">
          Vous pourrez tout modifier plus tard dans vos paramètres.
        </p>
      </div>

      <div className="flex flex-col gap-4">
        <button
          type="button"
          onClick={handleStart}
          disabled={busy}
          className="w-full px-5 py-4 bg-primary text-primary-foreground border-none rounded-full font-extrabold text-[16px] cursor-pointer min-h-[56px] tracking-[0.3px] transition-opacity disabled:opacity-[0.75] disabled:cursor-not-allowed"
        >
          {busy ? 'En cours…' : 'Commencer la configuration'}
        </button>
        <p className="font-[500] text-xs text-muted-foreground text-center m-0">
          En utilisant Naïa, vous acceptez nos{' '}
          <a href="/cgu" target="_blank" rel="noreferrer" className="text-naia-blue underline">
            conditions d'utilisation
          </a>
          {' '}et notre{' '}
          <a href="/confidentialite" target="_blank" rel="noreferrer" className="text-naia-blue underline">
            politique de confidentialité
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => void handleSignOut()}
          className="bg-transparent border-none font-semibold text-xs text-muted-foreground underline cursor-pointer py-2 min-h-[44px] mx-auto"
        >
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
